import { useState, useEffect } from 'react'
import { useAppGatewayCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { CurrencyTooltip } from '../shared/CurrencyTooltip'
import { FieldTooltip } from '../shared/FieldTooltip'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { DiscountModifier } from '../shared/DiscountModifier'
import { MobileCostBar } from '../shared/MobileCostBar'
import { FeedbackModal } from '../shared/FeedbackModal'
import { Disclaimer } from '../shared/Disclaimer'
import { ScenarioPresets } from '../shared/ScenarioPresets'
import { ResourcesSection } from '../shared/ResourcesSection'
import { ShareButton } from '../shared/ShareButton'
import { Slider } from '../ui/Slider'
import { Select } from '../ui/Select'
import type { AppGatewayRequest, AppGatewayScenario } from '../../api/types'
import { useCalculatorStore } from '../../store/calculatorStore'

const defaultInputs: AppGatewayRequest = {
  sku: 'standard_v2',
  minCapacityUnits: 2,
  avgCapacityUnits: 2,
  peakCapacityUnits: 5,
  peakHoursPerMonth: 40,
  monthlyDataProcessedTb: 1,
}

const SKU_OPTIONS = [
  { value: 'standard_v2', label: 'Standard v2', description: 'L7 load balancer, autoscale' },
  { value: 'waf_v2',      label: 'WAF v2',       description: 'Includes managed WAF rulesets (~1.8× cost)' },
] as const

function fmt(n: number) { return `£${n.toFixed(2)}` }

function ScenarioPanel({ scenarios }: { scenarios: AppGatewayScenario[] }) {
  if (!scenarios?.length) return null
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr>
            <th className="text-left py-2 pr-4 text-outline font-mono uppercase text-[10px] tracking-wider">Cost component</th>
            {scenarios.map((s) => (
              <th key={s.label} className="text-right py-2 px-3 text-outline font-mono uppercase text-[10px] tracking-wider whitespace-nowrap">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/20">
          <tr>
            <td className="py-2 pr-4 text-on-surface-variant">Gateway fee</td>
            {scenarios.map((s) => (
              <td key={s.label} className="py-2 px-3 text-right font-mono text-on-surface">{fmt(s.gatewayFee)}</td>
            ))}
          </tr>
          <tr>
            <td className="py-2 pr-4 text-on-surface-variant">Capacity Units</td>
            {scenarios.map((s) => (
              <td key={s.label} className="py-2 px-3 text-right font-mono text-on-surface">{fmt(s.capacityUnitsFee)}</td>
            ))}
          </tr>
          <tr>
            <td className="py-2 pr-4 text-on-surface-variant">Data processed</td>
            {scenarios.map((s) => (
              <td key={s.label} className="py-2 px-3 text-right font-mono text-on-surface">{fmt(s.dataFee)}</td>
            ))}
          </tr>
          <tr className="font-bold">
            <td className="py-2 pr-4 text-on-surface">Total</td>
            {scenarios.map((s, i) => (
              <td key={s.label} className={`py-2 px-3 text-right font-mono ${i === 0 ? 'text-primary' : i === 2 ? 'text-[#a4262c]' : 'text-[#e8b000]'}`}>
                {fmt(s.total)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default function AppGatewayCalc() {
  const [inputs, setInputs] = useState<AppGatewayRequest>(defaultInputs)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { discount, setDiscount } = useCalculatorStore()
  const { data, isLoading, isError } = useAppGatewayCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'application-gateway', result_gbp: data.monthly_estimate, tier: inputs.sku })
    }
  }, [data?.monthly_estimate])

  const visibleTotal = data ? data.monthly_estimate * (1 - discount / 100) : null

  function set<K extends keyof AppGatewayRequest>(key: K, value: AppGatewayRequest[K]) {
    setInputs((prev) => {
      const next = { ...prev, [key]: value }
      // Keep ordering invariants: avg >= min, peak >= avg
      if (key === 'minCapacityUnits' && next.avgCapacityUnits < next.minCapacityUnits) {
        next.avgCapacityUnits = next.minCapacityUnits
        if (next.peakCapacityUnits < next.avgCapacityUnits) next.peakCapacityUnits = next.avgCapacityUnits
      }
      if (key === 'avgCapacityUnits' && next.peakCapacityUnits < next.avgCapacityUnits) {
        next.peakCapacityUnits = next.avgCapacityUnits
      }
      return next
    })
  }

  const formulaLines = data ? [
    `Gateway fee: ${inputs.sku === 'standard_v2' ? 'Standard v2' : 'WAF v2'} × 730 hrs = £${data.rates?.gatewayHourly?.toFixed(4) ?? '?'}/hr × 730 = £${(data.rates?.gatewayHourly * 730).toFixed(2)}`,
    `Capacity Units: avg ${inputs.avgCapacityUnits} CU × ${730 - inputs.peakHoursPerMonth} hrs + peak ${inputs.peakCapacityUnits} CU × ${inputs.peakHoursPerMonth} hrs × £${data.rates?.cuHourly?.toFixed(4) ?? '?'}/CU-hr`,
    `Data: max(0, ${inputs.monthlyDataProcessedTb} TB − 10 TB free) × 1,024 × £${data.rates?.dataPerGb?.toFixed(4) ?? '?'}/GB`,
  ] : []

  return (
    <div className={`grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      <div className="flex flex-col gap-6">

        <ScenarioPresets presets={[
          {
            label: 'Small web app (Standard v2)',
            description: 'Standard v2 · avg 2 CU · 0.5 TB/mo — entry-level production gateway',
            apply: () => { set('sku', 'standard_v2'); set('minCapacityUnits', 1); set('avgCapacityUnits', 2); set('peakCapacityUnits', 5); set('peakHoursPerMonth', 50); set('monthlyDataProcessedTb', 0.5); },
          },
          {
            label: 'Enterprise WAF',
            description: 'WAF v2 · avg 10 CU · 5 TB/mo — shows WAF premium over Standard v2',
            apply: () => { set('sku', 'waf_v2'); set('minCapacityUnits', 2); set('avgCapacityUnits', 10); set('peakCapacityUnits', 25); set('peakHoursPerMonth', 100); set('monthlyDataProcessedTb', 5); },
          },
        ]} />

        {/* SKU */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>call_split</span>
            Gateway Configuration
          </h2>

          <Select
            label="SKU"
            value={inputs.sku}
            onChange={(value) => set('sku', value as AppGatewayRequest['sku'])}
            options={[...SKU_OPTIONS]}
          />

          {inputs.sku === 'waf_v2' && (
            <div className="mt-4 p-4 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">warning</span>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                <strong className="text-[#e8b000]">WAF v2 is ~1.8× the cost of Standard v2.</strong> Confirm you need the managed OWASP rulesets before selecting WAF v2 — for simple traffic routing, Standard v2 is sufficient.
              </p>
            </div>
          )}

          {data?.sku_comparison && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.sku_comparison.map((s) => (
                <div key={s.sku} className={`p-3 rounded-sm border text-center ${s.isSelected ? 'border-[#0078d4] bg-[#0078d4]/10' : 'border-outline-variant/30'}`}>
                  <div className="text-[10px] font-mono text-outline uppercase tracking-wider">{s.label}</div>
                  <div className={`text-xl font-bold font-mono mt-1 ${s.isSelected ? 'text-[#0078d4]' : 'text-on-surface-variant'}`}>
                    {fmt(s.total)}<span className="text-[11px] font-normal">/mo</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Capacity Units */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-[#107c10]" style={{ fontVariationSettings: "'FILL' 1" }}>speed</span>
            Capacity Units
          </h2>
          <p className="text-[12px] text-on-surface-variant mb-5 leading-relaxed">
            One Capacity Unit represents ~10 new connections/sec, 2,500 persistent connections, or 2.22 Mbps throughput.
            App Gateway autoscales between your minimum and maximum — you pay for actual CU-hours consumed.
          </p>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Minimum CUs (deployment floor) <FieldTooltip content="Capacity Units reserved at all times. Even at zero traffic you pay for this floor — set it to your idle resource needs." /></label>
                <span className="text-xl font-bold font-mono text-on-surface">{inputs.minCapacityUnits}</span>
              </div>
              <Slider value={inputs.minCapacityUnits} onChange={(v) => set('minCapacityUnits', v)} min={0} max={125} step={1} label="Min CUs" formatValue={(n) => String(n)} />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Average CUs (typical load) <FieldTooltip content="1 CU ≈ 2,500 persistent connections, 2.22 Mbps throughput, or 1 compute unit. Used for the non-peak portion of each month." /></label>
                <span className="text-xl font-bold font-mono text-on-surface">{inputs.avgCapacityUnits}</span>
              </div>
              <Slider value={inputs.avgCapacityUnits} onChange={(v) => set('avgCapacityUnits', Math.max(v, inputs.minCapacityUnits))} min={0} max={125} step={1} label="Avg CUs" formatValue={(n) => String(n)} />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Peak CUs (traffic spikes)</label>
                <span className="text-xl font-bold font-mono text-on-surface">{inputs.peakCapacityUnits}</span>
              </div>
              <Slider value={inputs.peakCapacityUnits} onChange={(v) => set('peakCapacityUnits', Math.max(v, inputs.avgCapacityUnits))} min={0} max={125} step={1} label="Peak CUs" formatValue={(n) => String(n)} />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Peak hours / month <FieldTooltip content="Hours per month where traffic spikes to the Peak CU level. Remaining hours are billed at the Average CU rate." /></label>
                <span className="text-xl font-bold font-mono text-on-surface">{inputs.peakHoursPerMonth} hrs</span>
              </div>
              <Slider value={inputs.peakHoursPerMonth} onChange={(v) => set('peakHoursPerMonth', v)} min={0} max={730} step={1} label="Peak hours" formatValue={(n) => `${n} hrs`} />
              <p className="text-[10px] text-outline italic">Default 40 hrs ≈ typical business-hours pattern. Remaining {730 - inputs.peakHoursPerMonth} hrs billed at average CUs.</p>
            </div>
          </div>
        </div>

        {/* Data processed */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-[#bc5b00]" style={{ fontVariationSettings: "'FILL' 1" }}>swap_horiz</span>
            Data Processed
          </h2>
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Monthly data processed</label>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold font-mono text-on-surface">{inputs.monthlyDataProcessedTb} TB</span>
                {inputs.monthlyDataProcessedTb <= 10 && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-[#107c10]/15 text-[#107c10] border border-[#107c10]/30">first 10 TB free</span>
                )}
              </div>
            </div>
            <Slider value={inputs.monthlyDataProcessedTb} onChange={(v) => set('monthlyDataProcessedTb', v)} min={0} max={200} step={0.5} label="Data processed" formatValue={(n) => `${n} TB`} />
          </div>
        </div>

        {/* Peak-vs-average comparison panel */}
        {data?.scenarios && (
          <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-[#a4262c]" style={{ fontVariationSettings: "'FILL' 1" }}>trending_up</span>
              Autoscale Cost Scenarios
            </h2>
            <p className="text-[12px] text-on-surface-variant mb-5 leading-relaxed">
              Your bill depends on how long the gateway runs at peak CUs. The table below shows your estimate under three load patterns.
            </p>
            <ScenarioPanel scenarios={data.scenarios} />
            <p className="text-[10px] text-outline italic mt-3">
              <strong>Typical:</strong> your inputs as-is. <strong>Peak-heavy:</strong> peak hours doubled, 1.5× data. <strong>Sustained load:</strong> peak CUs for the full month, 2× data.
            </p>
          </div>
        )}

        {/* Billing traps callout */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-[#a4262c]" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
            Common Billing Traps
          </h2>
          <ul className="flex flex-col gap-3">
            {[
              { title: 'Autoscale CU peak surprise', body: 'A traffic spike or DDoS event can sustain high CU for hours. At 125 CU for a full month, the CU cost alone is ~£657 on top of the base fee.' },
              { title: 'Standard v2 vs WAF v2 cost delta', body: 'WAF v2 is ~1.8× Standard v2 at the same capacity. For low-traffic workloads, the base hourly fee dominates — make sure you need the managed WAF rulesets.' },
              { title: 'Data processing beyond 10 TB', body: 'The first 10 TB/month of data processed through the gateway is free. High-throughput workloads can tip into billed territory without realising.' },
              { title: 'v1 to v2 migration billing model change', body: 'v1 gateways used fixed instance counts with predictable fees. v2 is autoscale-only — migrating from v1 to v2 introduces billing variability that surprises teams expecting a like-for-like swap.' },
            ].map(({ title, body }) => (
              <li key={title} className="flex items-start gap-3">
                <span className="material-symbols-outlined text-[#a4262c] text-[16px] shrink-0 mt-0.5">error</span>
                <div>
                  <span className="text-[12px] font-semibold text-on-surface">{title}</span>
                  <p className="text-[12px] text-on-surface-variant mt-0.5 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Cross-links */}
        <div className="flex flex-wrap gap-3">
          <a href="/firewall" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-3 py-2 border border-outline-variant/40 rounded-sm hover:border-[#0078d4] hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[14px]">shield</span>Compare with Azure Firewall
          </a>
          <a href="/front-door" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-3 py-2 border border-outline-variant/40 rounded-sm hover:border-[#0078d4] hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[14px]">public</span>Compare with Front Door
          </a>
        </div>

        <div className="lg:col-span-12">
          <ResourcesSection service="front-door" />
        </div>
      </div>

      {/* Results panel */}
      <div className="flex flex-col gap-4">
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 sticky top-20">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-headline font-bold text-lg">Estimated Cost</h2>
            <div className="flex items-center gap-2">
              <CurrencyTooltip />
              <ShareButton inputs={inputs} result={data ?? null} service="application-gateway" />
            </div>
          </div>

          {isError && (
            <p className="text-[12px] text-[#a4262c] mt-2">Error fetching prices. Check your connection.</p>
          )}

          {data?.warnings?.map((w) => (
            <div key={w} className="mt-3 p-3 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-2">
              <span className="material-symbols-outlined text-[#e8b000] text-[14px] shrink-0 mt-0.5">warning</span>
              <p className="text-[11px] leading-relaxed">{w}</p>
            </div>
          ))}

          <div className="mt-4 mb-4">
            <div className="text-[11px] font-mono text-outline uppercase tracking-wider mb-1">Monthly estimate</div>
            <div className="text-4xl font-bold font-mono text-primary">
              {visibleTotal !== null ? `£${visibleTotal.toFixed(2)}` : '—'}
            </div>
            <div className="text-[11px] text-outline mt-1">UK South · GBP · per month</div>
          </div>

          <DiscountModifier discount={discount} onDiscountChange={setDiscount} paygTotal={data?.monthly_estimate ?? 0} />

          {data?.breakdown && (
            <div className="mt-5 flex flex-col gap-2">
              {data.breakdown.map((item) => (
                <div key={item.label} className="flex justify-between items-start gap-2 text-[12px]">
                  <span className="text-on-surface-variant leading-snug">{item.label}</span>
                  <span className={`font-mono shrink-0 font-semibold ${item.isFree ? 'text-[#107c10]' : 'text-on-surface'}`}>
                    {item.isFree ? 'Free' : `£${item.value.toFixed(2)}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {data && (
            <FormulaDisclosure lines={formulaLines} fetchedAt={data.prices_last_updated ?? ''} />
          )}

          <div className="mt-5">
            <Disclaimer />
          </div>

          {data?.prices_last_updated && (
            <p className="text-[10px] text-outline mt-3">Prices last updated: {data.prices_last_updated}</p>
          )}

          <button
            className="mt-4 w-full text-[11px] font-mono uppercase tracking-widest text-outline hover:text-primary transition-colors py-1"
            onClick={() => setFeedbackOpen(true)}
          >
            Give feedback
          </button>
        </div>
      </div>

      <MobileCostBar amount={visibleTotal} label="Monthly estimate" targetId="app-gateway-results" />
      {feedbackOpen && (
        <FeedbackModal service="application-gateway" estimateValue={visibleTotal !== null ? `£${visibleTotal.toFixed(2)}/mo` : 'N/A'} onClose={() => setFeedbackOpen(false)} />
      )}
    </div>
  )
}
